'use client';

import { Image, RankingLabel, Typography } from '@musinsa/mds';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import { track } from '@musinsa/packages-event-snowplow';
import clsx from 'clsx';
import Link from 'next/link';
import type { MouseEventHandler } from 'react';
import { twMerge } from 'tailwind-merge';
import { useTrackingData } from '../../common/components/TrackingDataProvider';
import { useTrackImpression } from '../../common/hooks';
import { SNOWPLOW_SCHEME } from '../../snowplow';

interface Props {
  rank: number;
  imageUrl: string;
  brandName: string;
  goodsName: string;
  price: number;
  totalQuantity: number;
  totalCommission: number;
  linkUrl: string;
  goodsNo: number;
  className?: string;
}

export const ProductRankingItem = ({
  rank,
  imageUrl,
  brandName,
  goodsName,
  price,
  totalQuantity,
  totalCommission,
  linkUrl,
  goodsNo,
  className,
}: Props) => {
  const { service } = useDeviceInfo();
  const { data: trackingData } = useTrackingData();
  const { ref } = useTrackImpression<HTMLDivElement>({
    onImpression: () => {
      if (service === SERVICE['29CM']) {
        track({
          schema: SNOWPLOW_SCHEME.IMPRESSION_ITEM,
          data: {
            ...trackingData,
            item_no: goodsNo,
            item_position: rank,
            position: rank,
          },
        });
      }
    },
  });

  const handleClick: MouseEventHandler<HTMLAnchorElement> = () => {
    if (service === SERVICE['29CM']) {
      track({
        schema: SNOWPLOW_SCHEME.CLICK_ITEM,
        data: {
          ...trackingData,
          item_no: goodsNo,
          item_position: rank,
          position: rank,
        },
      });
    }
  };

  return (
    <Link href={linkUrl} className={twMerge('block', className)} onClick={handleClick}>
      <div ref={ref} className="flex items-center py-[8px]!">
        <div
          className={clsx(
            'relative rounded overflow-hidden shrink-0',

            service === SERVICE.MUSINSA ? 'w-[72px] h-[86px]' : 'w-[86px] h-[86px]',
          )}
        >
          <Image
            objectFit="cover"
            heightFull
            alt={goodsName}
            direction="horizontal"
            size="cover"
            src={imageUrl}
          />
          <div className="absolute top-1 left-1 z-10">
            <RankingLabel>{rank}</RankingLabel>
          </div>
        </div>
        <div className="flex flex-col w-full ml-3 overflow-hidden">
          <Typography size="13" weight="semiBold" className="truncate">
            {brandName}
          </Typography>
          <Typography size="13" weight="regular" className="truncate mt-[2px]">
            {goodsName}
          </Typography>
          <Typography size="13" weight="semiBold" className="mt-[2px]">
            {price.toLocaleString()}원
          </Typography>
          <Typography size="13" weight="regular" className="text-blue-600 mt-[2px]">
            {totalQuantity}건 판매 / {totalCommission.toLocaleString()}원
          </Typography>
        </div>
      </div>
    </Link>
  );
};
